import { useState } from 'react';

import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '../../hooks/useApiKeys';
import { ApiError } from '../../lib/api';
import { shortDate, timeAgo } from '../../lib/format';
import type { IssuedApiKey } from '../../types';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Note } from '../ui/Note';
import { Panel } from '../ui/Panel';
import { IssuedKeyReveal } from './IssuedKeyReveal';

const MAX_KEYS = 10;

/**
 * Create, list and revoke. The four operations `apps/api/src/routes/keys`
 * exposes and no more, there is deliberately no "show me the key again".
 *
 * Full secret + Copy / Copy as curl / Delete only exist on the issue reveal.
 */
export function ApiKeysPanel() {
    const { data, isPending } = useApiKeys();
    const createKey = useCreateApiKey();
    const revokeKey = useRevokeApiKey();
    const [name, setName] = useState('');
    const [issued, setIssued] = useState<IssuedApiKey | null>(null);

    const keys = data?.keys ?? [];
    const atLimit = keys.length >= MAX_KEYS;

    return (
        <Panel title="API keys" aside={`${keys.length} / ${MAX_KEYS}`}>
            {issued ? (
                <div className="mb-5">
                    <IssuedKeyReveal
                        issued={issued}
                        onDismiss={() => setIssued(null)}
                        deletePending={revokeKey.isPending}
                        onDelete={() => {
                            revokeKey.mutate(issued.apiKey.id, {
                                onSuccess: () => setIssued(null),
                            });
                        }}
                    />
                </div>
            ) : null}

            <form
                className="mb-5 flex flex-wrap items-end gap-3"
                onSubmit={(event) => {
                    event.preventDefault();
                    const trimmed = name.trim();
                    if (!trimmed) return;
                    createKey.mutate(trimmed, {
                        onSuccess: (result) => {
                            setIssued(result);
                            setName('');
                        },
                    });
                }}
            >
                <div className="min-w-48 flex-1">
                    <Input
                        label="New key"
                        value={name}
                        onChange={setName}
                        placeholder="production"
                        disabled={atLimit}
                        hint={atLimit ? `Revoke one before issuing another. All keys share your account quota.` : undefined}
                    />
                </div>
                <Button type="submit" disabled={createKey.isPending || atLimit || !name.trim()} size="sm">
                    {createKey.isPending ? 'Issuing…' : 'Issue key'}
                </Button>
            </form>

            {createKey.error ? (
                <div className="mb-5">
                    <Note tone="warn">
                        {createKey.error instanceof ApiError ? createKey.error.message : 'Something went wrong.'}
                    </Note>
                </div>
            ) : null}

            {isPending ? (
                <p className="text-sm text-text-muted">Loading…</p>
            ) : keys.length === 0 ? (
                <p className="text-sm text-text-muted">No live keys yet. Issue one above to call `/v1/*`.</p>
            ) : (
                <ul className="divide-y divide-border">
                    {keys.map((key) => (
                        <li key={key.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                            <div>
                                <p className="text-sm text-text">{key.name}</p>
                                <p className="font-mono text-xs text-text-muted">
                                    {key.keyPrefix}… · created {shortDate(key.createdAt)} · last used{' '}
                                    {timeAgo(key.lastUsedAt)}
                                </p>
                            </div>
                            <Button
                                variant="quiet"
                                size="sm"
                                disabled={revokeKey.isPending}
                                onClick={() => {
                                    if (confirm(`Revoke "${key.name}"? This takes effect immediately.`)) {
                                        revokeKey.mutate(key.id);
                                    }
                                }}
                            >
                                Revoke
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </Panel>
    );
}
